import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import CustomButton from '../../../components/customButton';
import deviceSteps from './deviceSteps';
import osSteps from './osSteps';
import './styles.scss';
import API from '../../../services/api';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedCompany } from '../../../redux/company/actions';
import { getSelectedProject } from '../../../redux/projects/actions';
import { getUser } from '../../../redux/user/actions';
import { setAlert } from '../../../redux/dashboard/actions';

const CreateDeviceModal = ({ isOpen, toggleModal, onCreate }) => {
  const [newDevice, setNewDevice] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [createdDevice, setCreatedDevice] = useState(null);

  const company = useSelector(getSelectedCompany);
  const user = useSelector(getUser);
  const project = useSelector(getSelectedProject);
  const dispatch = useDispatch();

  const deviceForProvision = createdDevice
    ? {
        ...createdDevice,
        applicationId: newDevice?.site?.applicationId,
        displayName: newDevice?.displayName || createdDevice.displayName,
        provisioningToken: project?.provisioningToken
      }
    : null;

  const selectOs = os => {
    setCreatedDevice(null);
    setNewDevice(currentDevice => ({
      ...currentDevice,
      os
    }));
    setCurrentStep(1);
  };

  const steps = [
    ...deviceSteps(setNewDevice, newDevice, selectOs),
    ...(deviceForProvision ? osSteps(newDevice?.os, deviceForProvision) : [])
  ];
  const selectedStep = steps[currentStep] || steps[0];
  const isNextDisabled = selectedStep.isNextDisabled ? selectedStep.isNextDisabled(newDevice) : false;

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setNewDevice(null);
      setCreatedDevice(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  const closeModal = () => {
    setCurrentStep(0);
    toggleModal();
  };

  const previous = () => {
    if (currentStep !== 0) {
      setCurrentStep(currentStep - 1);
    } else {
      closeModal();
    }
  };

  const createDevice = () => {
    setIsLoading(true);

    const deviceObject = {
      attachedSites: [newDevice.site.value],
      creatorId: user.id,
      displayName: newDevice.displayName,
      projectId: project.id
    };

    API.createDevice(company.id, deviceObject)
      .then(createdDevice => {
        setCreatedDevice(createdDevice);
        onCreate(createdDevice);
        setCurrentStep(currentStep + 1);
      })
      .catch(error => {
        dispatch(setAlert('Error creating device:', error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const next = () => {
    if (selectedStep.nextButtonLabel === 'Close') {
      closeModal();
    } else if (currentStep === 1 && newDevice?.os === 'linux' && !createdDevice) {
      createDevice();
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      closeModal();
    }
  };

  const getNextButtonTitle = () => {
    if (isLoading) {
      return 'Creating...';
    }

    if (currentStep === 1 && createdDevice) {
      return 'Next step';
    }

    return selectedStep.nextButtonLabel;
  };

  return (
    <Modal size="lg" isOpen={isOpen} fade={false} toggle={toggleModal} centered id="create-devices-modal">
      <ModalHeader>
        {selectedStep.title}
        <p className="create-modal-label">{selectedStep.label}</p>
      </ModalHeader>
      <ModalBody>{selectedStep.body}</ModalBody>
      <ModalFooter>
        {selectedStep.previousButtonLabel && (
          <CustomButton classes="btn btn-secondary" title={selectedStep.previousButtonLabel} handleClick={previous} />
        )}
        {selectedStep.nextButtonLabel && (
          <CustomButton
            classes="btn btn-primary"
            title={getNextButtonTitle()}
            handleClick={next}
            disabled={isNextDisabled || isLoading}
          />
        )}
      </ModalFooter>
    </Modal>
  );
};

export default CreateDeviceModal;
